import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, UserCheck, Loader2, AlertTriangle, Store, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";

type UserEntry = {
  id: number;
  email: string;
  name: string | null;
  fullName: string | null;
  company: string | null;
  selectedPlan: string | null;
  teamRole: string | null;
  teamOwnerEmail: string | null;
};

type VendorEntry = {
  id: number;
  name: string;
  businessName: string | null;
  phone: string | null;
  email: string | null;
};

type TeamMemberEntry = {
  id: number;
  vendorId: number;
  firstName: string;
  lastName: string;
  phone: string;
  role: string;
  status: string;
};

export default function AppDevToolsImpersonate() {
  const queryClient = useQueryClient();
  usePageTitle("Dev: Impersonate");
  const { toast } = useToast();

  // ── Host/user impersonation state ──
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserEntry | null>(null);
  const [password, setPassword] = useState("");
  const [showModal, setShowModal] = useState(false);

  // ── Vendor impersonation state ──
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState<VendorEntry | null>(null);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<string>("__owner__");
  const [vendorPassword, setVendorPassword] = useState("");
  const [showVendorModal, setShowVendorModal] = useState(false);

  // ── User list query ──
  const usersQuery = useQuery<{ users: UserEntry[] }>({
    queryKey: ["/api/app/impersonation/users", search],
    queryFn: async () => {
      const url = search
        ? `/api/app/impersonation/users?q=${encodeURIComponent(search)}`
        : "/api/app/impersonation/users";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  // ── Vendor list query ──
  const vendorsQuery = useQuery<{ vendors: VendorEntry[] }>({
    queryKey: ["/api/app/impersonation/vendors", vendorSearch],
    queryFn: async () => {
      const url = vendorSearch
        ? `/api/app/impersonation/vendors?q=${encodeURIComponent(vendorSearch)}`
        : "/api/app/impersonation/vendors";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  // ── Team members for selected vendor ──
  const teamMembersQuery = useQuery<TeamMemberEntry[]>({
    queryKey: ["/api/vendor-portal/team", selectedVendor?.id],
    queryFn: async () => {
      const res = await fetch(`/api/app/impersonation/vendor-team-members/${selectedVendor!.id}`, { credentials: "include" });
      if (!res.ok) return [];
      const data = await res.json();
      return data.members ?? [];
    },
    enabled: !!selectedVendor,
    staleTime: 30_000,
  });

  // ── User impersonation mutation ──
  const startMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ targetUserId, password }: { targetUserId: number; password: string }) => {
      const res = await apiRequest("POST", "/api/app/impersonation/start", { targetUserId, password });
      return res.json();
    },
    onSuccess: () => {
      setShowModal(false);
      setPassword("");
      queryClient.clear();
      window.location.assign("/app/dashboard");
    },
    onError: (err: Error) => {
      const msg = err.message.replace(/^\d+:\s*/, "").replace(/^\{.*"message":"([^"]+)".*\}$/, "$1");
      let parsed = msg;
      try { parsed = JSON.parse(msg)?.message ?? msg; } catch {}
      toast({ title: "Failed", description: parsed, variant: "destructive" });
    },
  });

  // ── Vendor impersonation mutation ──
  const startVendorMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ targetVendorId, targetVendorTeamMemberId, password }: { targetVendorId: number; targetVendorTeamMemberId?: number | null; password: string }) => {
      const res = await apiRequest("POST", "/api/app/impersonation/vendor/start", { targetVendorId, targetVendorTeamMemberId: targetVendorTeamMemberId ?? null, password });
      return res.json();
    },
    onSuccess: () => {
      setShowVendorModal(false);
      setVendorPassword("");
      queryClient.clear();
      window.location.assign("/vendor-portal");
    },
    onError: (err: Error) => {
      const msg = err.message.replace(/^\d+:\s*/, "").replace(/^\{.*"message":"([^"]+)".*\}$/, "$1");
      let parsed = msg;
      try { parsed = JSON.parse(msg)?.message ?? msg; } catch {}
      toast({ title: "Failed", description: parsed, variant: "destructive" });
    },
  });

  function openModal(user: UserEntry) {
    setSelectedUser(user);
    setPassword("");
    setShowModal(true);
  }

  function openVendorModal(vendor: VendorEntry) {
    setSelectedVendor(vendor);
    setSelectedTeamMemberId("__owner__");
    setVendorPassword("");
    setShowVendorModal(true);
  }

  function handleStart() {
    if (!selectedUser || !password) return;
    startMutation.mutate({ targetUserId: selectedUser.id, password });
  }

  function handleVendorStart() {
    if (!selectedVendor || !vendorPassword) return;
    const tmId = selectedTeamMemberId === "__owner__" ? null : parseInt(selectedTeamMemberId, 10);
    startVendorMutation.mutate({
      targetVendorId: selectedVendor.id,
      targetVendorTeamMemberId: tmId,
      password: vendorPassword,
    });
  }

  const users = usersQuery.data?.users ?? [];
  const vendors = vendorsQuery.data?.vendors ?? [];
  const teamMembers = (teamMembersQuery.data ?? []).filter((m) => m.status === "active" || m.status === "invited");

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-10">

      {/* ── Host / App User Impersonation ── */}
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900" data-testid="heading-impersonate">Impersonate User</h1>
          <p className="text-sm text-gray-500 mt-1">
            Search for any PadPulse account and start a temporary impersonation session. Sessions expire after 2 hours.
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Warning:</strong> Write actions (sending messages, creating records, syncing PadSplit) will affect real data. Stripe payment actions are blocked. All actions are logged for audit.
          </div>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="input-user-search"
            placeholder="Search by name, email, or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {usersQuery.isLoading && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading users…
          </div>
        )}

        {!usersQuery.isLoading && (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
            {users.length === 0 && (
              <div className="py-10 text-center text-sm text-gray-400">No users found</div>
            )}
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50" data-testid={`row-user-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{u.fullName || u.name || u.email}</div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                  {u.company && <div className="text-xs text-gray-400 truncate">{u.company}</div>}
                  {u.teamRole && u.teamOwnerEmail && (
                    <div className="text-xs text-blue-600 truncate">
                      Team {u.teamRole} of {u.teamOwnerEmail}
                    </div>
                  )}
                </div>
                {u.teamRole && (
                  <Badge variant="outline" className="shrink-0 text-xs border-blue-300 text-blue-700">Team</Badge>
                )}
                {u.selectedPlan && (
                  <Badge variant="secondary" className="shrink-0 text-xs">{u.selectedPlan}</Badge>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openModal(u)}
                  data-testid={`button-impersonate-${u.id}`}
                >
                  <UserCheck className="h-4 w-4 mr-1" />
                  Impersonate
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Vendor Impersonation ── */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" data-testid="heading-impersonate-vendor">
            <Store className="h-6 w-6" />
            Impersonate Vendor
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Step into any vendor's portal session. You can optionally target a specific team member within the vendor. Sessions expire after 2 hours.
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Warning:</strong> You will have the same access level as the impersonated vendor or team member. All write actions trigger a confirmation prompt. Navigates to <code>/vendor-portal</code> after start.
          </div>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="input-vendor-search"
            placeholder="Search vendors by name, phone, or email…"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {vendorsQuery.isLoading && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading vendors…
          </div>
        )}

        {!vendorsQuery.isLoading && (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
            {vendors.length === 0 && (
              <div className="py-10 text-center text-sm text-gray-400">No vendors found</div>
            )}
            {vendors.map((v) => (
              <div key={v.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50" data-testid={`row-vendor-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{v.name}</div>
                  {v.businessName && <div className="text-xs text-gray-500 truncate">{v.businessName}</div>}
                  {v.phone && <div className="text-xs text-gray-400 truncate">{v.phone}</div>}
                  {v.email && <div className="text-xs text-gray-400 truncate">{v.email}</div>}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openVendorModal(v)}
                  data-testid={`button-impersonate-vendor-${v.id}`}
                >
                  <Store className="h-4 w-4 mr-1" />
                  Impersonate
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── User Impersonation Confirm Modal ── */}
      <Dialog open={showModal} onOpenChange={(v) => { setShowModal(v); if (!v) setPassword(""); }}>
        <DialogContent data-testid="dialog-impersonate-confirm">
          <DialogHeader>
            <DialogTitle>Confirm Impersonation</DialogTitle>
            <DialogDescription>
              You are about to impersonate <strong>{selectedUser?.email}</strong>. Enter your own password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Your password</label>
              <Input
                type="password"
                data-testid="input-impersonate-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleStart()}
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowModal(false)} data-testid="button-cancel-impersonate">
                Cancel
              </Button>
              <Button
                onClick={handleStart}
                disabled={!password || startMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="button-confirm-impersonate"
              >
                {startMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Start Impersonation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Vendor Impersonation Confirm Modal ── */}
      <Dialog open={showVendorModal} onOpenChange={(v) => { setShowVendorModal(v); if (!v) setVendorPassword(""); }}>
        <DialogContent data-testid="dialog-vendor-impersonate-confirm">
          <DialogHeader>
            <DialogTitle>Confirm Vendor Impersonation</DialogTitle>
            <DialogDescription>
              You are about to impersonate vendor <strong>{selectedVendor?.name}</strong>
              {selectedVendor?.businessName && ` (${selectedVendor.businessName})`}.
              Optionally select a team member, then enter your password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Team member (optional)</label>
              {teamMembersQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading team members…
                </div>
              ) : (
                <Select value={selectedTeamMemberId} onValueChange={setSelectedTeamMemberId}>
                  <SelectTrigger data-testid="select-team-member">
                    <SelectValue placeholder="Act as owner (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__owner__">Act as owner (default)</SelectItem>
                    {teamMembers.map((tm) => (
                      <SelectItem key={tm.id} value={String(tm.id)}>
                        {tm.firstName} {tm.lastName} — {tm.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Your password</label>
              <Input
                type="password"
                data-testid="input-vendor-impersonate-password"
                placeholder="Enter your password"
                value={vendorPassword}
                onChange={(e) => setVendorPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleVendorStart()}
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowVendorModal(false)} data-testid="button-cancel-vendor-impersonate">
                Cancel
              </Button>
              <Button
                onClick={handleVendorStart}
                disabled={!vendorPassword || startVendorMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="button-confirm-vendor-impersonate"
              >
                {startVendorMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Start Vendor Impersonation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
